import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import html2pdf from "html2pdf.js";
import { toast } from "react-toastify";
import { getToken } from "../../../utils/tokenService";
import { getBackendUrl } from "../../../utils/backendUrl";
import { savePdfBlob } from "../../../utils/nativePdfSave";
import {
  completeDownloadNotification,
  failDownloadNotification,
  startDownloadNotification,
} from "../../../utils/downloadNotification";

const defaultReportColumns = [
  { key: "slNo", label: "S.No" },
  { key: "employeeCode", label: "Employee ID" },
  { key: "employeeName", label: "Employee" },
  { key: "fatherName", label: "Father Name" },
  { key: "designation", label: "Designation" },
  { key: "gender", label: "Gender" },
  { key: "payableDays", label: "Payable Days" },
  { key: "presentDays", label: "Present" },
  { key: "absentDays", label: "Absent" },
  { key: "leaveDays", label: "Leave" },
  { key: "totalHours", label: "Hours" },
  { key: "overtimeHours", label: "OT Hours" },
  { key: "basic", label: "Basic" },
  { key: "da", label: "DA" },
  { key: "hra", label: "HRA" },
  { key: "leaveWithWages", label: "LWW" },
  { key: "otherAllowance", label: "Other Allowance" },
  { key: "grossWages", label: "Gross Wages" },
  { key: "uanNumber", label: "UAN" },
  { key: "epfNumber", label: "EPF" },
  { key: "esiNumber", label: "ESI" },
  { key: "bankAccount", label: "Bank Account" },
  { key: "ifsc", label: "IFSC" },
  { key: "leaveApplied", label: "Leave Applied" },
  { key: "leaveApproved", label: "Leave Approved" },
  { key: "leaveRejected", label: "Leave Rejected" },
  { key: "leavePending", label: "Leave Pending" },
  { key: "status", label: "Status" },
];

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const getReportTemplate = (report) => {
  const reportType = normalizeText(report?.reportType);
  const formType = normalizeText(report?.formType);
  const combined = `${reportType} ${formType}`;
  const isFormXVI = /\bform xvi\b/.test(formType);
  const isFormXVII = /\bform xvii\b/.test(formType);
  const isFormXIV = /\bform xiv\b/.test(formType);
  const isFormXIII = /\bform xiii\b/.test(formType);
  const isFormXX = /\bform xx\b/.test(formType);
  const isFormXXI = /\bform xxi\b/.test(formType);
  const isFormXXII = /\bform xxii\b/.test(formType);
  const isFormXXIII = /\bform xxiii\b/.test(formType);
  const isFormXIX = /\bform xix\b/.test(formType);

  if (reportType.includes("register of wages") || isFormXVII) {
    return "wage-register";
  }
  if (reportType.includes("muster roll") || isFormXVI) {
    return "muster-roll";
  }
  if (reportType.includes("esic form 6") || formType.includes("form 6")) {
    return "esic-form-6";
  }
  if (reportType.includes("leave with wages") || formType.includes("leave with wages")) {
    return "leave-wages-register";
  }
  if (reportType.includes("employment card") || isFormXIV) {
    return "employment-card";
  }
  if (reportType.includes("leave") || combined.includes("leave register")) {
    return "leave-register";
  }
  if (reportType.includes("register of hra")) return "hra-register";
  if (reportType.includes("advance") || isFormXXII) return "advance-register";
  if (reportType.includes("fine") || isFormXXI) return "fine-register";
  if (isFormXX || reportType.includes("deduction") || reportType.includes("damage")) {
    return "damage-deduction-register";
  }
  if (isFormXXIII || reportType.includes("ot ") || reportType.includes("overtime")) {
    return "overtime-register";
  }
  if (reportType.includes("accident")) return "accident-register";
  if (reportType.includes("bonus")) return "bonus-register";
  if (reportType.includes("equal remuneration")) return "equal-remuneration";
  if (reportType.includes("wage slips") || isFormXIX) return "wage-slip";
  if (reportType.includes("register of workman") || isFormXIII) {
    return "workman-register";
  }
  if (reportType.includes("labour welfare")) return "welfare-register";
  if (reportType.includes("unpaid accumulations")) return "unpaid-register";
  if (reportType.includes("workman compensation")) return "compensation-register";
  if (reportType.includes("nomination") || reportType.includes("declaration")) {
    return "nomination-register";
  }
  return "standard-register";
};

const getEarnedWages = (row, report) => {
  const daysInMonth = Number(report?.daysInMonth) || 0;
  const payableDays = Number(row.payableDays) || 0;
  const ratio = daysInMonth ? Math.min(payableDays / daysInMonth, 1) : 0;
  const earned = (value) => Math.round((Number(value) || 0) * ratio);
  const basic = earned(row.basic);
  const hra = earned(row.hra);
  const leaveWithWages = earned(row.leaveWithWages);
  const otherAllowance = earned(row.otherAllowance);
  const monthlyGross = basic + hra + leaveWithWages + otherAllowance;
  const employerEsic = Math.round(monthlyGross * 0.0325);
  const employerPf = Math.round(monthlyGross * 0.12);
  const adminCharge = Math.round(monthlyGross * 0.01);
  const employeeEsic = Math.round(monthlyGross * 0.0075);
  const employeePf = Math.round(monthlyGross * 0.12);
  const deductions = employeeEsic + employeePf;

  return {
    basic,
    hra,
    leaveWithWages,
    otherAllowance,
    monthlyGross,
    employerEsic,
    employerPf,
    adminCharge,
    employeeEsic,
    employeePf,
    deductions,
    netTakeHome: monthlyGross - deductions,
  };
};

const getReportColumns = (report) => {
  const template = getReportTemplate(report);

  if (template === "muster-roll") {
    const dayColumns = Array.from(
      { length: report?.daysInMonth || 31 },
      (_, index) => ({
        key: `day${index + 1}`,
        label: String(index + 1),
        getValue: (row) => row.dailyAttendance?.[index + 1] || "",
      })
    );

    return [
      { key: "employeeCode", label: "Employee ID" },
      { key: "employeeName", label: "Name" },
      { key: "fatherName", label: "Father Name" },
      { key: "designation", label: "Designation" },
      { key: "gender", label: "Gender" },
      ...dayColumns,
      { key: "presentDays", label: "Present days" },
      { key: "halfDays", label: "Half Day" },
      { key: "publicHolidays", label: "Public Holiday" },
      { key: "payableDays", label: "Paid Days" },
      { key: "remarks", label: "Remarks" },
    ];
  }

  if (template === "esic-form-6") {
    return [
      { key: "slNo", label: "S.No" },
      { key: "employeeCode", label: "Employee ID" },
      { key: "employeeName", label: "Name of Employee" },
      { key: "fatherName", label: "Father Name" },
      { key: "gender", label: "Gender" },
      { key: "designation", label: "Designation" },
      { key: "payableDays", label: "Payable Days" },
      { key: "grossWages", label: "Gross Wages" },
      { key: "uanNumber", label: "UAN" },
      { key: "epfNumber", label: "EPF" },
      { key: "esiNumber", label: "ESI No." },
      { key: "bankAccount", label: "Bank Account" },
      { key: "ifsc", label: "IFSC" },
    ];
  }

  if (template === "employment-card") {
    return [
      { key: "slNo", label: "S.No" },
      { key: "employeeCode", label: "Employee ID" },
      { key: "employeeName", label: "Name of workman" },
      { key: "designation", label: "Nature of employment/designation" },
      { key: "grossWages", label: "Wage rate" },
      { key: "dateOfJoining", label: "Date of joining" },
      { key: "remarks", label: "Remarks" },
    ];
  }

  if (template === "wage-register") {
    return [
      { key: "slNo", label: "S.No", group: "Employee Identification" },
      { key: "employeeCode", label: "Employee ID", group: "Employee Identification" },
      { key: "employeeName", label: "Name of employee", group: "Employee Identification" },
      { key: "fatherName", label: "Father Name", group: "Employee Identification" },
      { key: "designation", label: "Designation", group: "Employee Identification" },
      { key: "uanNumber", label: "UAN No.", group: "Employee Identification" },
      { key: "totalWorkingDays", label: "Total working days", group: "Days", getValue: (_, report) => report.daysInMonth || "" },
      { key: "payableDays", label: "No. of Payable days", group: "Days" },
      { key: "basic", label: "Basic", group: "Actual Salary Rate" },
      { key: "hra", label: "HRA", group: "Actual Salary Rate" },
      { key: "leaveWithWages", label: "LWW", group: "Actual Salary Rate" },
      { key: "otherAllowance", label: "Other Allow.", group: "Actual Salary Rate" },
      { key: "grossWages", label: "Monthly Gross", group: "Actual Salary Rate" },
      { key: "earnedBasic", label: "Basic", group: "Earned Salary", getValue: (row, report) => getEarnedWages(row, report).basic },
      { key: "earnedHra", label: "HRA", group: "Earned Salary", getValue: (row, report) => getEarnedWages(row, report).hra },
      { key: "earnedLww", label: "LWW", group: "Earned Salary", getValue: (row, report) => getEarnedWages(row, report).leaveWithWages },
      { key: "earnedOther", label: "Other Allow.", group: "Earned Salary", getValue: (row, report) => getEarnedWages(row, report).otherAllowance },
      { key: "incentive", label: "Incentive", group: "Earned Salary", getValue: () => 0 },
      { key: "foodReimbursement", label: "Food Reimb.", group: "Earned Salary", getValue: () => 0 },
      { key: "earnedGross", label: "Monthly Gross", group: "Earned Salary", getValue: (row, report) => getEarnedWages(row, report).monthlyGross },
      { key: "employerEsic", label: "ESIC (3.25%)", group: "Employer Contribution", getValue: (row, report) => getEarnedWages(row, report).employerEsic },
      { key: "employerPf", label: "PF (12%)", group: "Employer Contribution", getValue: (row, report) => getEarnedWages(row, report).employerPf },
      { key: "adminCharge", label: "Admin Charge (1%)", group: "Employer Contribution", getValue: (row, report) => getEarnedWages(row, report).adminCharge },
      { key: "employeeEsic", label: "ESIC (0.75%)", group: "Employee Contribution", getValue: (row, report) => getEarnedWages(row, report).employeeEsic },
      { key: "employeePf", label: "PF (12%)", group: "Employee Contribution", getValue: (row, report) => getEarnedWages(row, report).employeePf },
      { key: "deductions", label: "Deductions", getValue: (row, report) => getEarnedWages(row, report).deductions },
      { key: "netTakeHome", label: "Net Take Home", getValue: (row, report) => getEarnedWages(row, report).netTakeHome },
      { key: "signature", label: "Signature or Thumb impression", getValue: () => "" },
      { key: "initials", label: "Initials of contractor or representative", getValue: () => "" },
    ];
  }

  if (template === "leave-register") {
    return [
      { key: "slNo", label: "S.No" },
      { key: "employeeCode", label: "Employee ID" },
      { key: "employeeName", label: "Employee" },
      { key: "fatherName", label: "Father Name" },
      { key: "designation", label: "Designation" },
      { key: "leaveApplied", label: "Applied" },
      { key: "leaveApproved", label: "Approved" },
      { key: "leaveRejected", label: "Rejected" },
      { key: "leavePending", label: "Pending" },
      { key: "casualLeave", label: "Casual Used" },
      { key: "earnedLeave", label: "Earned Used" },
      { key: "medicalLeave", label: "Medical Used" },
    ];
  }

  if (template === "leave-wages-register") {
    return [
      { key: "slNo", label: "S.No" },
      { key: "employeeCode", label: "Employee ID" },
      { key: "employeeName", label: "Name of worker" },
      { key: "fatherName", label: "Father's name" },
      { key: "calendarYear", label: "Calendar year of service" },
      { key: "wagePeriod", label: "Wage paid from - to" },
      { key: "grossWages", label: "Wages earned during wage period" },
      { key: "payableDays", label: "No. of days of work performed" },
      { key: "leaveApplied", label: "No. of days lay-off / maternity leave" },
      { key: "leaveApproved", label: "No. of days leave enjoyed" },
      { key: "leaveCredit", label: "Balance of leave from preceding year" },
      { key: "earnedLeave", label: "Leave earned during year" },
      { key: "leaveTotal", label: "Total leave to credit" },
      { key: "leaveRejected", label: "Leave refused" },
      { key: "leavePending", label: "Leave enjoyed from - to" },
      { key: "leaveBalance", label: "Balance of leave to credit" },
      { key: "normalRate", label: "Normal rate of wages" },
      { key: "cashEquivalent", label: "Cash equivalent" },
      { key: "leaveRate", label: "Rate of wages for leave period" },
      { key: "remarks", label: "Remarks" },
    ];
  }

  if (
    ["hra-register", "bonus-register", "welfare-register", "unpaid-register"].includes(
      template
    )
  ) {
    return [
      { key: "slNo", label: "S.No" },
      { key: "employeeCode", label: "Employee ID" },
      { key: "employeeName", label: "Name of Employee" },
      { key: "fatherName", label: "Father Name" },
      { key: "designation", label: "Designation" },
      { key: "payableDays", label: "Payable Days" },
      { key: "basic", label: "Basic Wages" },
      { key: "hra", label: "HRA" },
      { key: "grossWages", label: "Gross Wages" },
      { key: "remarks", label: "Remarks" },
    ];
  }

  if (template === "workman-register") {
    return [
      { key: "slNo", label: "S.No" },
      { key: "employeeName", label: "Name and surname of workmen" },
      { key: "dateOfBirth", label: "Age / Date of birth" },
      { key: "gender", label: "Sex" },
      { key: "fatherName", label: "Father's / husband's name" },
      { key: "designation", label: "Nature of employment / designation" },
      { key: "permanentAddress", label: "Permanent home address" },
      { key: "localAddress", label: "Local address" },
      { key: "dateOfJoining", label: "Date of commencement of employment" },
      { key: "signature", label: "Signature or thumb impression" },
      { key: "terminationDate", label: "Date of termination" },
      { key: "terminationReason", label: "Reasons for termination" },
      { key: "remarks", label: "Remarks" },
    ];
  }

  if (template === "damage-deduction-register") {
    return [
      { key: "slNo", label: "Serial no." },
      { key: "employeeName", label: "Name of workmen" },
      { key: "fatherName", label: "Father's / Husband's name" },
      { key: "designation", label: "Designation / nature of employment" },
      { key: "damageParticulars", label: "Particulars of damage or loss" },
      { key: "damageDate", label: "Date of damage or loss" },
      { key: "showCause", label: "Whether workmen showed cause against deductions" },
      { key: "hearingPerson", label: "Name of person in whose presence employee's explanation was heard" },
      { key: "deductionAmount", label: "Amount of deduction imposed" },
      { key: "recoveryDate", label: "No. of instalments / date of recovery" },
      { key: "firstInstalment", label: "First instalment" },
      { key: "lastInstalment", label: "Last instalment" },
      { key: "remarks", label: "Remarks" },
    ];
  }

  if (template === "advance-register") {
    return [
      { key: "slNo", label: "Serial no." },
      { key: "employeeName", label: "Name" },
      { key: "fatherName", label: "Father's / Husband's name" },
      { key: "designation", label: "Nature of employment / designation" },
      { key: "wagePeriod", label: "Wage period and wages payable", getValue: (row, report) => `${report.monthLabel || report.month} / ${row.grossWages || 0}` },
      { key: "advanceGiven", label: "Date and amount of advance given" },
      { key: "advancePurpose", label: "Purpose for which advance given" },
      { key: "instalments", label: "Number of instalments" },
      { key: "instalmentsRepaid", label: "Date and amount of each instalment repaid" },
      { key: "lastInstalmentDate", label: "Date last instalment was repaid" },
      { key: "remarks", label: "Remarks" },
    ];
  }

  if (template === "fine-register") {
    return [
      { key: "slNo", label: "Serial no." },
      { key: "employeeName", label: "Name of workmen" },
      { key: "fatherName", label: "Father's / Husband's name" },
      { key: "designation", label: "Designation / nature of employment" },
      { key: "actOrOmission", label: "Act / omission for which fine imposed" },
      { key: "offenceDate", label: "Date of offence" },
      { key: "showCause", label: "Whether workman showed cause against fine" },
      { key: "hearingPerson", label: "Name of person in whose presence explanation was heard" },
      { key: "wagePeriod", label: "Wage period and wages payable", getValue: (row, report) => `${report.monthLabel || report.month} / ${row.grossWages || 0}` },
      { key: "fineAmount", label: "Amount of fine imposed" },
      { key: "fineRealisedDate", label: "Date fine realised" },
      { key: "remarks", label: "Remarks" },
    ];
  }

  if (template === "overtime-register") {
    return [
      { key: "slNo", label: "S.No" },
      { key: "employeeName", label: "Name of workman" },
      { key: "fatherName", label: "Father's / Husband's name" },
      { key: "gender", label: "Sex" },
      { key: "designation", label: "Designation / nature of employment" },
      { key: "overtimeDates", label: "Dates on which overtime worked" },
      { key: "overtimeHours", label: "Total overtime worked" },
      { key: "normalRate", label: "Normal rate of wages", getValue: (row) => row.grossWages || 0 },
      { key: "overtimeRate", label: "Overtime rate of wages", getValue: (row) => (Number(row.grossWages) || 0) * 2 },
      { key: "overtimeEarnings", label: "Overtime earnings", getValue: (row) => Math.round((Number(row.overtimeHours) || 0) * ((Number(row.grossWages) || 0) / 26 / 8) * 2) },
      { key: "overtimePaidDate", label: "Date overtime wages paid" },
      { key: "remarks", label: "Remarks" },
    ];
  }

  if (template === "deduction-register") {
    return [
      { key: "slNo", label: "S.No" },
      { key: "employeeCode", label: "Employee ID" },
      { key: "employeeName", label: "Name of Employee" },
      { key: "fatherName", label: "Father Name" },
      { key: "designation", label: "Designation" },
      { key: "grossWages", label: "Monthly Wages" },
      { key: "payableDays", label: "Payable Days" },
      { key: "remarks", label: "Particulars / Remarks" },
    ];
  }

  if (template === "wage-slip") {
    return [
      { key: "slNo", label: "S.No" },
      { key: "employeeCode", label: "Employee ID" },
      { key: "employeeName", label: "Name of Employee" },
      { key: "designation", label: "Designation" },
      { key: "payableDays", label: "Payable Days" },
      { key: "basic", label: "Basic" },
      { key: "hra", label: "HRA" },
      { key: "otherAllowance", label: "Other Allowance" },
      { key: "grossWages", label: "Gross Wages" },
      { key: "epfNumber", label: "EPF" },
      { key: "esiNumber", label: "ESI" },
      { key: "bankAccount", label: "Bank Account" },
      { key: "remarks", label: "Remarks" },
    ];
  }

  if (
    [
      "equal-remuneration",
      "accident-register",
      "workman-register",
      "compensation-register",
      "nomination-register",
    ].includes(template)
  ) {
    return [
      { key: "slNo", label: "S.No" },
      { key: "employeeCode", label: "Employee ID" },
      { key: "employeeName", label: "Name of Employee" },
      { key: "fatherName", label: "Father / Guardian Name" },
      { key: "gender", label: "Gender" },
      { key: "designation", label: "Designation" },
      { key: "contact", label: "Contact" },
      { key: "uanNumber", label: "UAN / Reference No." },
      { key: "status", label: "Status" },
      { key: "remarks", label: "Remarks" },
    ];
  }

  return defaultReportColumns;
};

const getColumnValue = (row, column, report) =>
  column.getValue ? column.getValue(row, report) : row[column.key];

const getFormHeader = (report) => {
  const template = getReportTemplate(report);

  if (template === "muster-roll") {
    return {
      formNo: "Form XVI",
      title: "MUSTER ROLL",
      rule: "See Rule 78(1)(a) (i)",
    };
  }

  if (template === "esic-form-6") {
    return {
      formNo: "FORM 6",
      title: "EMPLOYEES REGISTER",
      rule: "Employees' State Insurance",
    };
  }

  if (template === "employment-card") {
    return {
      formNo: "Form XIV",
      title: "EMPLOYMENT CARD",
      rule: "See Rule 76",
    };
  }

  if (template === "workman-register") {
    return {
      formNo: "Form XIII",
      title: "REGISTER OF WORKMEN EMPLOYED BY CONTRACTOR",
      rule: "See Rule 75",
    };
  }

  if (template === "damage-deduction-register") {
    return {
      formNo: "Form XX",
      title: "REGISTER OF DEDUCTIONS FOR DAMAGE OR LOSS",
      rule: "See Rule 78 (2) (b)",
    };
  }

  if (template === "wage-register") {
    return {
      formNo: "Form XVII",
      title: "REGISTER OF WAGES",
      rule: "See Rule 78(2)(a)",
    };
  }

  if (template === "leave-register") {
    return {
      formNo: "Form XV",
      title: "LEAVE REGISTER",
      rule: "Monthly Leave Record",
    };
  }

  if (template === "leave-wages-register") {
    return {
      formNo: "Form 15",
      title: "REGISTER OF LEAVE WITH WAGES FOR THE YEAR",
      rule: "Rule 120",
    };
  }

  const statutoryHeaders = {
    "advance-register": ["Form XXII", "REGISTER OF ADVANCE", "See Rule 78 (2) (d)"],
    "fine-register": ["Form XXI", "REGISTER OF FINES", "See Rule 78 (2) (d)"],
    "overtime-register": ["Form XXIII", "REGISTER OF OVERTIME", "See Rule 78 (2) (c)"],
    "wage-slip": ["Form XIX", "WAGE SLIP", "See Rule 78(1)(b)"],
    "accident-register": ["Form 11", "ACCIDENT BOOK", "ESI Regulation 66"],
    "bonus-register": ["Form C", "BONUS PAID TO EMPLOYEES", "See Rule 4(C)"],
    "equal-remuneration": ["Form D", "EQUAL REMUNERATION REGISTER", "See Rule 6"],
    "unpaid-register": ["Form C", "REGISTER OF UNPAID ACCUMULATIONS", "Rule 21"],
  };

  if (statutoryHeaders[template]) {
    const [formNo, title, rule] = statutoryHeaders[template];
    return { formNo, title, rule };
  }

  if (["hra-register", "welfare-register", "compensation-register", "nomination-register"].includes(template)) {
    return {
      formNo: report?.formType || "Compliance Register",
      title: report?.reportType || "COMPLIANCE REGISTER",
      rule: "Employee Record",
    };
  }

  return {
    formNo: report?.formType || "Compliance Form",
    title: report?.reportType || "COMPLIANCE REPORT",
    rule: "Monthly Register",
  };
};

const escapeCsvCell = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const formatCardDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB");
};

const getEmploymentTenure = (value) => {
  if (!value) return "-";
  const start = new Date(value);
  if (Number.isNaN(start.getTime())) return "-";
  const now = new Date();
  const months = Math.max(
    0,
    (now.getFullYear() - start.getFullYear()) * 12 + now.getMonth() - start.getMonth()
  );
  if (months < 12) return `${months} Month${months === 1 ? "" : "s"}`;
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  return `${years} Year${years === 1 ? "" : "s"}${remainingMonths ? ` ${remainingMonths} Month${remainingMonths === 1 ? "" : "s"}` : ""}`;
};

const ReportPrintView = React.forwardRef(({ report, columns }, ref) => {
  const header = getFormHeader(report);
  const columnGroups = columns.reduce((groups, column) => {
    const previous = groups[groups.length - 1];
    if (column.group && previous?.label === column.group) {
      previous.count += 1;
    } else if (column.group) {
      groups.push({ label: column.group, count: 1 });
    } else {
      groups.push({ label: "", count: 1 });
    }
    return groups;
  }, []);
  const isWageRegister = header.formNo === "Form XVII";
  const isEmploymentCard = header.formNo === "Form XIV";
  const isWorkmanRegister = header.formNo === "Form XIII";
  const isDamageDeductionRegister = header.formNo === "Form XX";
  const isLeaveWagesRegister =
    header.formNo === "Form 15" && getReportTemplate(report) === "leave-wages-register";
  const contractorAddress = [
    report.contractor?.name,
    report.contractor?.address,
    report.contractor?.contact ? `Contact: ${report.contractor.contact}` : "",
    report.contractor?.email ? `Email: ${report.contractor.email}` : "",
  ]
    .filter(Boolean)
    .join(", ");
  const principalAddress = [
    report.principalEmployer?.name,
    report.principalEmployer?.address,
  ]
    .filter(Boolean)
    .join(", ");

  if (isEmploymentCard) {
    return (
      <div className="employment-card-report" ref={ref}>
        {(report.rows || []).map((row) => (
          <section className="employment-card-page" key={`${row.employeeCode}-${row.slNo}`}>
            <div className="employment-card-title">
              <div>{header.formNo}</div>
              <div>[{header.rule}]</div>
              <div>{header.title}</div>
            </div>
            <div className="employment-card-line">
              <strong>Name and address of Contractor:</strong> {contractorAddress || "-"}
            </div>
            <div className="employment-card-line">
              <strong>Name and address of establishment in/under which contractor is carried on:</strong> {principalAddress || "-"}
            </div>
            <div className="employment-card-line">
              <strong>Nature and Location of worked:</strong> {report.natureAndLocation || report.location || "-"}
            </div>
            <div className="employment-card-line">
              <strong>Name and address of Principal Employer:</strong> {principalAddress || "-"}
            </div>
            <table className="employment-card-table">
              <tbody>
                <tr><td>1. Name of the workmen</td><td>{row.employeeName || "-"}</td></tr>
                <tr><td>2. Serial no. in the register of workmen employed</td><td>{row.employeeCode || row.slNo}</td></tr>
                <tr><td>3. Nature of employment/designation</td><td>{row.designation || "-"}</td></tr>
                <tr><td>4. Wage rate with particulars of unit, in case of piece-work</td><td>{row.grossWages || 0}</td></tr>
                <tr><td>5. Wage period</td><td>{report.monthLabel || report.month}</td></tr>
                <tr><td>6. Date of joining / tenure of employment</td><td>{formatCardDate(row.dateOfJoining)} / {getEmploymentTenure(row.dateOfJoining)}</td></tr>
                <tr><td>7. Remarks</td><td>{row.remarks || ""}</td></tr>
              </tbody>
            </table>
          </section>
        ))}
      </div>
    );
  }

  if (isWorkmanRegister) {
    return (
      <div className="workman-register-report" ref={ref}>
        <div className="workman-register-title">
          <div>{header.formNo}</div>
          <div>[{header.rule}]</div>
          <div>{header.title}</div>
        </div>
        <div className="workman-register-meta">
          <div><strong>Name and address of Contractor:</strong> {contractorAddress || "-"}</div>
          <div><strong>Name and address of establishment in/under which contractor is carried on:</strong> {principalAddress || "-"}</div>
          <div><strong>Nature and Location of worked:</strong> {report.natureAndLocation || report.location || "-"}</div>
          <div><strong>Name and address of Principal Employer:</strong> {principalAddress || "-"}</div>
        </div>
        <table className="workman-register-table">
          <thead>
            <tr>
              {columns.map((column) => <th key={column.key}>{column.label}</th>)}
            </tr>
            <tr className="workman-register-numbers">
              {columns.map((_, index) => <th key={index}>{index + 1}</th>)}
            </tr>
          </thead>
          <tbody>
            {(report.rows || []).map((row) => (
              <tr key={`${row.employeeCode}-${row.slNo}`}>
                <td>{row.employeeCode || row.slNo}</td>
                <td>{row.employeeName || ""}</td>
                <td>{formatCardDate(row.dateOfBirth)}</td>
                <td>{row.gender || ""}</td>
                <td>{row.fatherName || ""}</td>
                <td>{row.designation || ""}</td>
                <td>{row.permanentAddress || ""}</td>
                <td>{row.localAddress || ""}</td>
                <td>{formatCardDate(row.dateOfJoining)}</td>
                <td></td>
                <td></td>
                <td></td>
                <td>{row.remarks || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (isDamageDeductionRegister) {
    const blankRows = Array.from({ length: 10 }, (_, index) => index);
    return (
      <div className="damage-deduction-report" ref={ref}>
        <div className="damage-deduction-title">
          <div>{header.formNo}</div>
          <div>[{header.rule}]</div>
          <div>{header.title}</div>
        </div>
        <div className="damage-deduction-meta">
          <div><strong>Name and address of Contractor:</strong> {contractorAddress || "-"}</div>
          <div><strong>Name and address of establishment in/under which contractor is carried on:</strong> {principalAddress || "-"}</div>
          <div><strong>Nature and Location of worked:</strong> {report.natureAndLocation || report.location || "-"}</div>
          <div><strong>Name and address of Principal Employer:</strong> {principalAddress || "-"}</div>
        </div>
        <table className="damage-deduction-table">
          <thead>
            <tr>
              {columns.map((column) => <th key={column.key}>{column.label}</th>)}
            </tr>
            <tr className="damage-deduction-numbers">
              {columns.map((_, index) => <th key={index}>{index + 1}</th>)}
            </tr>
          </thead>
          <tbody>
            {blankRows.map((row) => (
              <tr key={row}>
                {columns.map((column) => <td key={column.key}></td>)}
              </tr>
            ))}
            <tr className="damage-deduction-certification">
              <td colSpan={columns.length}>
                Certified that no deduction for damage or loss has been made during {report.monthLabel || report.month}.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  if (isLeaveWagesRegister) {
    const year = String(report.month || "").slice(0, 4) || new Date().getFullYear();
    const periodStart = report.monthLabel || report.month;
    const periodEnd = report.monthLabel || report.month;

    return (
      <div className="leave-wages-report" ref={ref}>
        <section className="leave-wages-page">
          <div className="leave-wages-title">
            <div>{header.formNo}</div>
            <div>[{header.rule}]</div>
            <div>{header.title} ({year}-{Number(year) + 1})</div>
          </div>
          <div className="leave-wages-details">
            <div><strong>Name and address of the establishment:</strong> {principalAddress || "-"}</div>
            <div><strong>Department:</strong> {report.location || "-"}</div>
            <div><strong>Nature and location of work:</strong> {report.natureAndLocation || report.location || "-"}</div>
            <div><strong>Principal Employer:</strong> {principalAddress || "-"}</div>
          </div>
          <table className="leave-wages-table">
            <thead>
              <tr>{columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr>
              <tr className="leave-wages-numbers">{columns.map((_, index) => <th key={index}>{index + 1}</th>)}</tr>
            </thead>
            <tbody>
              {(report.rows || []).map((row) => {
                const leaveToCredit = (Number(row.earnedLeave) || 0) + (Number(row.leaveApproved) || 0);
                const leaveBalance = Math.max(0, leaveToCredit - (Number(row.leaveApproved) || 0));
                const wageRate = row.payableDays ? Math.round((Number(row.grossWages) || 0) / Number(row.payableDays)) : 0;
                const values = [
                  row.slNo,
                  row.employeeCode,
                  row.employeeName,
                  row.fatherName,
                  year,
                  `${periodStart} to ${periodEnd}`,
                  row.grossWages || 0,
                  row.payableDays || 0,
                  0,
                  row.leaveApproved || 0,
                  0,
                  row.earnedLeave || 0,
                  leaveToCredit,
                  row.leaveRejected || 0,
                  "",
                  leaveBalance,
                  wageRate,
                  0,
                  wageRate,
                  row.remarks || "",
                ];
                return <tr key={`${row.employeeCode}-${row.slNo}`}>{values.map((value, index) => <td key={index}>{value}</td>)}</tr>;
              })}
            </tbody>
          </table>
        </section>
      </div>
    );
  }

  return (
    <div
      className={`statutory-report-page ${isWageRegister ? "form-xvii-report" : ""}`}
      ref={ref}
    >
      <div className="statutory-title">
        <div>{header.formNo}</div>
        <div>{header.title}</div>
        <div>{header.rule}</div>
        <div>For the Month of :- {report.monthLabel || report.month}</div>
      </div>

      {isWageRegister ? (
        <div className="form-xvii-meta">
          <div className="form-xvii-label">Name and Address of Contractor&nbsp; :-</div>
          <div className="form-xvii-value">{contractorAddress || "-"}</div>
          <div className="form-xvii-label">
            Name and address of the shop/commercial establishment in /Under Which
            Contract is Carried on :-
          </div>
          <div className="form-xvii-value">{principalAddress || "-"}</div>
          <div className="form-xvii-work">
            Nature and Location of Work :- {report.natureAndLocation || report.location}
          </div>
          <div className="form-xvii-label">Name and address of Principal Employer:-</div>
          <div className="form-xvii-value">{principalAddress || "-"}</div>
        </div>
      ) : (
        <div className="statutory-meta-grid">
          <div className="statutory-meta-label">Name and Address of Contractor&nbsp; :-</div>
          <div className="statutory-meta-value">{contractorAddress || "-"}</div>
          <div className="statutory-meta-side">
            <div>
              Name and address of the shop/commercial establishment in /Under Which
              Contract is Carried on :-
            </div>
            <strong>{principalAddress || "-"}</strong>
          </div>
          <div className="statutory-meta-label small-meta">
            Nature and Location of Work :- {report.natureAndLocation || report.location}
          </div>
          <div className="statutory-meta-side lower">
            <div>Name and address of Principal Employer:-</div>
            <strong>{principalAddress || "-"}</strong>
          </div>
        </div>
      )}

      {isWageRegister && (
        <div className="statutory-period">
          Wages period Monthly: {report.monthLabel || report.month}
        </div>
      )}

      <div className="statutory-table-wrap">
        <table className={`statutory-table ${isWageRegister ? "wage-register-table" : ""}`}>
          <thead>
            {isWageRegister && (
              <tr className="statutory-group-row">
                {columnGroups.map((group, index) => (
                  <th colSpan={group.count} key={`${group.label}-${index}`}>
                    {group.label}
                  </th>
                ))}
              </tr>
            )}
            <tr>
              {columns.map((column) => (
                <th key={column.key}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(report.rows || []).map((row) => (
              <tr key={`${row.employeeCode}-${row.slNo}`}>
                {columns.map((column) => (
                  <td key={column.key}>{getColumnValue(row, column, report) ?? ""}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

const Reports = () => {
  const reportRefs = useRef({});
  const [clients, setClients] = useState([]);
  const [reportTypes, setReportTypes] = useState([]);
  const [formTypes, setFormTypes] = useState([]);
  const [filters, setFilters] = useState({
    client: "",
    location: "",
    month: "",
    reportType: "",
    formType: "",
  });
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [downloadStatus, setDownloadStatus] = useState("");
  const backendUrl = useMemo(() => getBackendUrl(), []);

  useEffect(() => {
    const loadReportOptions = async () => {
      try {
        setLoadingOptions(true);
        const token = await getToken();
        const res = await axios.get(
          `${backendUrl}/api/establishment/reports/options`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setClients(res.data?.clients || []);
        setReportTypes(res.data?.reportTypes || []);
        setFormTypes(res.data?.formTypes || []);
      } catch (err) {
        toast.error(
          err?.response?.data?.message || "Could not load report filters"
        );
      } finally {
        setLoadingOptions(false);
      }
    };

    loadReportOptions();
  }, [backendUrl]);

  const selectedClient = useMemo(
    () => clients.find((client) => client._id === filters.client),
    [clients, filters.client]
  );

  const locations = useMemo(
    () => selectedClient?.locations || [],
    [selectedClient]
  );

  const filteredFormTypes = useMemo(() => {
    if (!filters.reportType) return formTypes;

    return formTypes.filter((formType) => {
      const reportTypeId =
        formType.reportType?._id || formType.reportType || "";
      return reportTypeId === filters.reportType;
    });
  }, [filters.reportType, formTypes]);

  const handleChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "client" ? { location: "" } : {}),
      ...(field === "reportType" ? { formType: "" } : {}),
    }));
    setResults([]);
    setLastUpdated(null);
  };

  const search = useCallback(async (silent = false) => {
    if (!filters.client || !filters.month || !filters.reportType || !filters.formType) {
      if (!silent) {
        toast.error("Please select client, month, report type, and form type");
      }
      return;
    }

    try {
      setLoading(true);
      const token = await getToken();
      const res = await axios.post(
        `${backendUrl}/api/establishment/reports/search`,
        filters,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const reports = (res.data?.reports || []).map((report) => {
        const rows = (report.rows || []).filter(
          (row) => Number(row.payableDays) > 0
        );

        return {
          ...report,
          rows,
          totalEmployees: rows.length,
          status: rows.length ? "Ready" : "No employee data",
        };
      });
      setResults(reports);
      setLastUpdated(new Date());
      if (!reports.length && !silent) toast.info("No reports found for selected filters.");
    } catch (err) {
      if (!silent) {
        toast.error(err?.response?.data?.message || "Could not search reports");
      }
    } finally {
      setLoading(false);
    }
  }, [backendUrl, filters]);

  useEffect(() => {
    if (!results.length) return undefined;

    const refreshTimer = window.setInterval(() => search(true), 30000);
    return () => window.clearInterval(refreshTimer);
  }, [results.length, search]);

  const downloadCsv = (report) => {
    const rows = report.rows || [];
    if (!rows.length) {
      toast.info("No rows available to download.");
      return;
    }

    const documentName = `${report.formType || "Report"} report`;
    const notificationId = startDownloadNotification(documentName);
    const columns = getReportColumns(report);
    const header = columns.map((column) => column.label);
    const csvRows = rows.map((row) =>
      columns.map((column) => escapeCsvCell(getColumnValue(row, column, report))).join(",")
    );
    const csv = [header.join(","), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const fileDate = report.month || filters.month || "report";
    link.href = url;
    link.download = `${report.formType || "report"}-${fileDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => URL.revokeObjectURL(url), 1500);
    setDownloadStatus(`${documentName} downloaded.`);
    completeDownloadNotification(notificationId, documentName);
  };

  const downloadPdf = async (report, index) => {
    const node = reportRefs.current[index];
    if (!node) {
      toast.error("Report preview is not ready yet.");
      return;
    }

    const filename = `${report.formType || "report"}-${report.month || filters.month}.pdf`
      .replace(/[\\/:*?"<>|]+/g, "-");
    const documentName = `${report.formType || "Report"} report`;
    const pxToMm = 0.264583;
    const reportWidthPx = Math.max(node.scrollWidth, node.offsetWidth, 1180);
    const reportHeightPx = Math.max(node.scrollHeight, node.offsetHeight, 700);
    const pdfWidthMm = Math.max(297, reportWidthPx * pxToMm + 10);
    const pdfHeightMm = Math.max(210, reportHeightPx * pxToMm + 10);
    const pdfOptions = {
      margin: [3, 3, 3, 3],
      filename,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        windowWidth: reportWidthPx,
        width: reportWidthPx,
      },
      jsPDF: {
        unit: "mm",
        format: [pdfWidthMm, pdfHeightMm],
        orientation: pdfWidthMm >= pdfHeightMm ? "landscape" : "portrait",
      },
      pagebreak: { mode: ["css", "legacy"] },
    };

    let notificationId;

    try {
      setDownloadStatus(`${documentName} is downloading...`);
      notificationId = startDownloadNotification(documentName);
      const clone = node.cloneNode(true);
      clone.classList.add("pdf-export-report");
      clone.style.width = `${reportWidthPx}px`;
      clone.style.maxWidth = "none";
      const tableWrap = clone.querySelector(".statutory-table-wrap");
      if (tableWrap) tableWrap.style.overflow = "visible";
      const host = document.createElement("div");
      host.className = "pdf-export-host";
      host.style.width = `${reportWidthPx}px`;
      host.appendChild(clone);
      document.body.appendChild(host);

      const blob = await html2pdf()
        .set(pdfOptions)
        .from(clone)
        .outputPdf("blob");
      const saved = await savePdfBlob(blob, filename);
      if (!saved) {
        throw new Error("PDF could not be saved");
      }
      document.body.removeChild(host);
      setDownloadStatus(`${documentName} downloaded.`);
      completeDownloadNotification(notificationId, documentName, { onClick: saved?.open });
    } catch (error) {
      const host = document.querySelector(".pdf-export-host");
      if (host) document.body.removeChild(host);
      console.error("Report PDF download failed:", error);
      setDownloadStatus(`Could not download ${documentName.toLowerCase()}.`);
      if (notificationId) {
        failDownloadNotification(notificationId, documentName);
      } else {
        toast.error(`Could not download ${documentName.toLowerCase()}.`);
      }
    }
  };

  return (
    <div className="container py-4 reports-page">
      <style>{`
        .reports-page .report-action-primary {
          background: #2563eb !important;
          border-color: #2563eb !important;
          color: #fff !important;
        }

        .reports-page .report-action-primary:hover,
        .reports-page .report-action-primary:focus {
          background: #1d4ed8 !important;
          border-color: #1d4ed8 !important;
          box-shadow: none !important;
          color: #fff !important;
        }

        .reports-page .report-action-secondary {
          background: #2563eb !important;
          border-color: #2563eb !important;
          color: #fff !important;
        }

        .reports-page .report-action-secondary:hover,
        .reports-page .report-action-secondary:focus {
          background: #1d4ed8 !important;
          border-color: #1d4ed8 !important;
          box-shadow: none !important;
          color: #fff !important;
        }

        .reports-page .report-action-primary:disabled,
        .reports-page .report-action-secondary:disabled {
          background: #e5e7eb !important;
          border-color: #d1d5db !important;
          color: #6b7280 !important;
        }

        .reports-page .form-control,
        .reports-page .form-select {
          background-color: #fff;
          border-color: #000;
          color: #000;
        }

        .reports-page .report-result-card {
          min-width: 0;
          overflow: hidden;
        }

        .reports-page .report-preview-frame {
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          border: 1px solid #000;
          background: #fff;
        }

        .reports-page .report-preview-frame::-webkit-scrollbar {
          height: 8px;
        }

        .reports-page .report-preview-frame::-webkit-scrollbar-thumb {
          background: #777;
        }

        .statutory-report-page {
          width: 1180px;
          max-width: none;
          background: #fff !important;
          color: #000 !important;
          border: 1px solid #000;
          font-family: Arial, Helvetica, sans-serif;
          overflow-x: auto;
        }

        .statutory-report-page *,
        .employment-card-report,
        .employment-card-report *,
        .workman-register-report,
        .workman-register-report *,
        .damage-deduction-report,
        .damage-deduction-report *,
        .leave-wages-report,
        .leave-wages-report * {
          background: #fff !important;
          color: #000 !important;
          border-color: #000 !important;
        }

        .form-xvii-report {
          width: 1280px;
        }

        .employment-card-report {
          width: 100%;
          max-width: 740px;
          background: #fff;
          color: #000;
        }

        .employment-card-page {
          background: #fff;
          color: #000;
          border: 1px solid #000;
          font-family: Arial, Helvetica, sans-serif;
          font-size: 12px;
          line-height: 1.35;
          margin-bottom: 16px;
          break-after: page;
          page-break-after: always;
        }

        .employment-card-page:last-child {
          break-after: auto;
          page-break-after: auto;
        }

        .employment-card-title {
          border-bottom: 1px solid #000;
          font-weight: 700;
          text-align: center;
          padding: 10px 8px 8px;
          line-height: 1.35;
        }

        .employment-card-line {
          border-bottom: 1px solid #000;
          min-height: 36px;
          padding: 8px 10px;
        }

        .employment-card-table {
          border-collapse: collapse;
          width: 100%;
        }

        .employment-card-table td {
          border: 1px solid #000;
          min-height: 26px;
          padding: 3px 8px;
          vertical-align: middle;
        }

        .employment-card-table td:first-child {
          width: 62%;
        }

        .workman-register-report {
          width: 1280px;
          background: #fff;
          color: #000;
          border: 1px solid #000;
          font-family: Arial, Helvetica, sans-serif;
        }

        .workman-register-title {
          border-bottom: 1px solid #000;
          padding: 4px 8px;
          font-size: 10px;
          font-weight: 700;
          line-height: 1.2;
          text-align: center;
        }

        .workman-register-meta {
          border-bottom: 1px solid #000;
          font-size: 10px;
          line-height: 1.3;
        }

        .workman-register-meta div {
          min-height: 17px;
          padding: 2px 4px;
        }

        .workman-register-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          font-size: 8px;
          line-height: 1.15;
        }

        .workman-register-table th,
        .workman-register-table td {
          border: 1px solid #000;
          padding: 3px 2px;
          vertical-align: middle;
          text-align: center;
          word-break: break-word;
        }

        .workman-register-table th {
          font-weight: 500;
        }

        .workman-register-table th:nth-child(1) { width: 4%; }
        .workman-register-table th:nth-child(2) { width: 9%; }
        .workman-register-table th:nth-child(3) { width: 5%; }
        .workman-register-table th:nth-child(4) { width: 3%; }
        .workman-register-table th:nth-child(5) { width: 9%; }
        .workman-register-table th:nth-child(6) { width: 6%; }
        .workman-register-table th:nth-child(7) { width: 11%; }
        .workman-register-table th:nth-child(8) { width: 17%; }
        .workman-register-table th:nth-child(9) { width: 6%; }
        .workman-register-table th:nth-child(10) { width: 10%; }
        .workman-register-table th:nth-child(11) { width: 6%; }
        .workman-register-table th:nth-child(12) { width: 7%; }
        .workman-register-table th:nth-child(13) { width: 7%; }

        .workman-register-numbers th {
          padding: 1px;
          font-size: 8px;
        }

        .damage-deduction-report {
          width: 1180px;
          background: #fff;
          color: #000;
          border: 1px solid #000;
          font-family: Arial, Helvetica, sans-serif;
        }

        .damage-deduction-title {
          border-bottom: 1px solid #000;
          padding: 4px 8px;
          font-size: 10px;
          font-weight: 700;
          line-height: 1.2;
          text-align: center;
        }

        .damage-deduction-meta {
          border-bottom: 1px solid #000;
          font-size: 10px;
          line-height: 1.3;
        }

        .damage-deduction-meta div {
          min-height: 20px;
          padding: 3px 6px;
        }

        .damage-deduction-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          font-size: 8px;
          line-height: 1.2;
        }

        .damage-deduction-table th,
        .damage-deduction-table td {
          border: 1px solid #000;
          min-height: 18px;
          padding: 3px 2px;
          text-align: center;
          vertical-align: middle;
          word-break: break-word;
        }

        .damage-deduction-table th {
          font-weight: 500;
        }

        .damage-deduction-table th:nth-child(1) { width: 3%; }
        .damage-deduction-table th:nth-child(2) { width: 6%; }
        .damage-deduction-table th:nth-child(3) { width: 8%; }
        .damage-deduction-table th:nth-child(4) { width: 8%; }
        .damage-deduction-table th:nth-child(5) { width: 10%; }
        .damage-deduction-table th:nth-child(6) { width: 7%; }
        .damage-deduction-table th:nth-child(7) { width: 11%; }
        .damage-deduction-table th:nth-child(8) { width: 12%; }
        .damage-deduction-table th:nth-child(9) { width: 9%; }
        .damage-deduction-table th:nth-child(10) { width: 8%; }
        .damage-deduction-table th:nth-child(11) { width: 7%; }
        .damage-deduction-table th:nth-child(12) { width: 6%; }
        .damage-deduction-table th:nth-child(13) { width: 5%; }

        .damage-deduction-numbers th {
          padding: 1px;
          font-size: 8px;
        }

        .damage-deduction-certification td {
          height: 28px;
          font-weight: 700;
          text-align: center;
        }

        .leave-wages-report {
          width: 1500px;
          background: #fff;
          color: #000;
          font-family: Arial, Helvetica, sans-serif;
        }

        .leave-wages-page {
          border: 1px solid #000;
          margin-bottom: 16px;
          break-after: page;
          page-break-after: always;
        }

        .leave-wages-page:last-child {
          break-after: auto;
          page-break-after: auto;
        }

        .leave-wages-title {
          border-bottom: 1px solid #000;
          padding: 4px;
          font-size: 10px;
          font-weight: 700;
          line-height: 1.2;
          text-align: center;
        }

        .leave-wages-details {
          display: grid;
          grid-template-columns: 42% 18% 20% 20%;
          font-size: 9px;
          line-height: 1.25;
        }

        .leave-wages-details div {
          border-right: 1px solid #000;
          border-bottom: 1px solid #000;
          min-height: 24px;
          padding: 3px 4px;
        }

        .leave-wages-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          font-size: 8px;
          line-height: 1.2;
        }

        .leave-wages-table th,
        .leave-wages-table td {
          border: 1px solid #000;
          padding: 3px 2px;
          text-align: center;
          vertical-align: middle;
          word-break: break-word;
        }

        .leave-wages-table th {
          height: 132px;
          font-weight: 500;
        }

        .leave-wages-numbers th {
          height: auto;
          padding: 1px;
          font-size: 8px;
        }

        @media (max-width: 767px) {
          .reports-page .report-preview-frame {
            border-left: 0;
            border-right: 0;
          }

          .employment-card-report {
            min-width: 620px;
          }
        }

        .statutory-title {
          text-align: center;
          font-size: 11px;
          line-height: 1.05;
          font-weight: 700;
          padding: 4px 8px 3px;
          border-bottom: 1px solid #111;
        }

        .statutory-meta-grid {
          display: grid;
          grid-template-columns: 36% 34% 30%;
          border-bottom: 1px solid #111;
          font-size: 11px;
          font-weight: 700;
          line-height: 1.25;
        }

        .statutory-meta-grid > div {
          border-right: 1px solid #111;
          min-height: 70px;
          padding: 4px;
        }

        .statutory-meta-grid > div:nth-child(3),
        .statutory-meta-grid > div:nth-child(5) {
          border-right: 0;
        }

        .statutory-meta-label {
          display: flex;
          align-items: center;
        }

        .statutory-meta-value {
          word-break: break-word;
        }

        .statutory-meta-side {
          grid-row: span 1;
          word-break: break-word;
        }

        .form-xvii-meta {
          display: grid;
          grid-template-columns: 13% 35% 17% 35%;
          font-size: 8px;
          font-weight: 700;
          line-height: 1.25;
          border-bottom: 1px solid #000;
        }

        .form-xvii-meta > div {
          min-height: 56px;
          padding: 4px;
          word-break: break-word;
        }

        .form-xvii-label {
          text-align: center;
          align-self: center;
        }

        .form-xvii-value {
          align-self: center;
        }

        .form-xvii-work {
          grid-column: 1 / span 2;
          min-height: 30px !important;
          border-top: 1px solid #000;
          display: flex;
          align-items: center;
        }

        .form-xvii-work + .form-xvii-label,
        .form-xvii-work + .form-xvii-label + .form-xvii-value {
          min-height: 30px !important;
          border-top: 1px solid #000;
        }

        .small-meta {
          grid-column: 1 / span 2;
          min-height: 38px !important;
          border-top: 1px solid #111;
        }

        .lower {
          min-height: 38px !important;
          border-top: 1px solid #111;
        }

        .statutory-table-wrap {
          overflow-x: auto;
        }

        .statutory-period {
          padding: 4px;
          border-bottom: 1px solid #000;
          font-size: 11px;
          font-weight: 700;
        }

        .statutory-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: auto;
          font-size: 9px;
          line-height: 1.1;
        }

        .statutory-table th,
        .statutory-table td {
          border: 1px solid #111;
          padding: 2px 3px;
          white-space: nowrap;
          text-align: center;
          vertical-align: middle;
        }

        .statutory-table th {
          font-weight: 700;
          background: #fff;
        }

        .statutory-group-row th {
          white-space: normal;
          min-width: 48px;
        }

        .wage-register-table {
          min-width: 1280px;
          table-layout: fixed;
          font-size: 7px;
        }

        .wage-register-table th,
        .wage-register-table td {
          white-space: normal;
          min-width: 0;
          padding: 2px 1px;
        }

        .wage-register-table th:nth-child(3),
        .wage-register-table td:nth-child(3),
        .wage-register-table th:nth-child(4),
        .wage-register-table td:nth-child(4),
        .wage-register-table th:nth-child(5),
        .wage-register-table td:nth-child(5) {
          min-width: 85px;
        }

        .statutory-table th:nth-child(2),
        .statutory-table td:nth-child(2),
        .statutory-table th:nth-child(3),
        .statutory-table td:nth-child(3),
        .statutory-table th:nth-child(4),
        .statutory-table td:nth-child(4) {
          text-align: left;
        }

        .pdf-export-host {
          position: fixed;
          left: -10000px;
          top: 0;
          width: 1180px;
          background: #fff;
          z-index: -1;
        }

        .pdf-export-report {
          max-width: none;
          overflow: visible;
        }

        .pdf-export-report .statutory-table-wrap {
          overflow: visible;
        }

        @media print {
          .statutory-report-page {
            max-width: none;
            width: 1180px;
          }
        }
      `}</style>
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <h5 className="mb-3">Reports</h5>
          <div className="row g-3 align-items-end">
            <div className="col-md-4 col-xl-2">
              <label className="form-label">Client</label>
              <select
                className="form-select"
                value={filters.client}
                onChange={(e) => handleChange("client", e.target.value)}
                disabled={loadingOptions}
              >
                <option value="">Select client</option>
                {clients.map((client) => (
                  <option key={client._id} value={client._id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4 col-xl-2">
              <label className="form-label">Location</label>
              <select
                className="form-select"
                value={filters.location}
                onChange={(e) => handleChange("location", e.target.value)}
                disabled={!filters.client || loadingOptions}
              >
                <option value="">All locations</option>
                {locations.map((location) => (
                  <option key={location._id} value={location._id}>
                    {location.location}
                    {location.state ? `, ${location.state}` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4 col-xl-2">
              <label className="form-label">Month</label>
              <input
                type="month"
                className="form-control"
                value={filters.month}
                onChange={(e) => handleChange("month", e.target.value)}
              />
            </div>

            <div className="col-md-4 col-xl-2">
              <label className="form-label">Report Type</label>
              <select
                className="form-select"
                value={filters.reportType}
                onChange={(e) => handleChange("reportType", e.target.value)}
                disabled={loadingOptions}
              >
                <option value="">Select report</option>
                {reportTypes.map((reportType) => (
                  <option key={reportType._id} value={reportType._id}>
                    {reportType.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4 col-xl-2">
              <label className="form-label">Form Type</label>
              <select
                className="form-select"
                value={filters.formType}
                onChange={(e) => handleChange("formType", e.target.value)}
                disabled={
                  loadingOptions || !filters.reportType || !filteredFormTypes.length
                }
              >
                <option value="">
                  {filters.reportType ? "Select form" : "Select report first"}
                </option>
                {filteredFormTypes.map((formType) => (
                  <option key={formType._id} value={formType._id}>
                    {formType.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4 col-xl-2 d-flex">
              <button
                className="btn report-action-primary ms-auto w-100"
                onClick={() => search()}
                disabled={loading || loadingOptions}
              >
                {loading ? "Searching..." : "Search"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <h6 className="mb-3">Results</h6>
          {!results.length ? (
            <p className="text-muted mb-0">
              Select filters above and click Search to view reports.
            </p>
          ) : (
            <div className="d-flex flex-column gap-3">
              {results.map((report, index) => {
                const columns = getReportColumns(report);
                const isBlankStatutoryRegister =
                  getReportTemplate(report) === "damage-deduction-register";
                const hasPrintableReport =
                  Boolean(report.rows?.length) || isBlankStatutoryRegister;
                return (
                <div className="border rounded p-3 report-result-card" key={`${report.formType}-${index}`}>
                   <div className="d-flex align-items-center justify-content-end gap-2 flex-wrap mb-3">
                     {downloadStatus && (
                       <span className="small text-primary me-auto" role="status" aria-live="polite">
                         {downloadStatus}
                       </span>
                     )}
                    <button
                      className="btn report-action-primary btn-sm"
                      onClick={() => search()}
                      disabled={loading}
                    >
                      {loading ? "Refreshing..." : "Refresh"}
                    </button>
                    <button
                      className="btn report-action-secondary btn-sm"
                      onClick={() => downloadCsv(report)}
                      disabled={!report.rows?.length}
                    >
                      Download CSV
                    </button>
                    <button
                      className="btn report-action-primary btn-sm"
                      onClick={() => downloadPdf(report, index)}
                      disabled={!hasPrintableReport}
                    >
                      Download PDF
                    </button>
                  </div>
                  {lastUpdated && (
                    <div className="small text-muted text-end mb-2">
                      Updated {lastUpdated.toLocaleTimeString()}
                    </div>
                  )}
                  {!hasPrintableReport ? (
                    <p className="text-muted mb-0">No employee rows found for this selection.</p>
                  ) : (
                    <div className="report-preview-frame">
                      <ReportPrintView
                        ref={(node) => {
                          if (node) reportRefs.current[index] = node;
                        }}
                        report={report}
                        columns={columns}
                      />
                    </div>
                  )}
                </div>
              );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
